import { useAppSelector, usePermission, useUser } from "@/OneLiners";
import { ActionResponse, ActionStatusHelper, UIFeedbackHooks, useAppEnum } from "@/Shared";
import { useEffect, useState } from "react";
import { UserViewModel } from "../ViewModels";
import { useAccessRight } from "@/Domains/Auth";

/**
 * useUserProfile hook – Manages user list configuration, state, and interactions.
 *
 * This hook encapsulates all the logic required to:
 * - Fetch and enrich user entities
 * - Provide configuration for a DataTable or DataCardList component
 * - Handle row actions: view, create, edit, delete
 * - Manage toast notifications for user feedback
 * - Report fetch/delete status for conditional UI rendering
 *
 * Exposes:
 * - userConfig: DataTableConfig object used by UI components
 * - isFetchRunning, isFetchFailed, fetchErrorMessage: status of fetch operation
 * - isDeleteSucceeded, isDeleteFailed, deleteErrorMessage: status of deletion
 * - setDeleteIdle: resets delete status to 'idle' (used to clear state)
 * 
 * Internally, it:
 * - Enriches users using `useEnrichedUser`
 * - Builds table headers with user data and enums
 * 
 * Author: Samano CASTRE  
 * Date: August 3, 2025
 */
export function useUserProfile(
	uiFeedbackHooks: UIFeedbackHooks = {} as UIFeedbackHooks
) {

	const currentUser = useAppSelector(state => state.sessionState.userSession?.createdByUser);


	/**
	   * Permissions data (used to compute access rights)
	   */
	const {
		dataMap: permissionMap,
		stateResponse : permissionResponse
	} = usePermission();


	/**
	 * User domain CRUD operations and state
	 */
	const {
		queryId,
		stateResponse : userResponse
	} = useUser();

	/**
		 * Access rights resolved from permissions
		 */
	const accessRight = useAccessRight(permissionMap);

	/**
		 * User-related enums exposed to the UI layer
		 */
	const { UserEnums } = useAppEnum();

	const [isDropdownOpen, setIsDropdownOpen] = useState(false);

	const [viewModel, setViewModel] = useState<UserViewModel>();


	const response: ActionResponse = {
		status: ActionStatusHelper.combine(
			userResponse.status,
			permissionResponse.status
		),
		error: userResponse.error ??
			 permissionResponse.error
	};

	useEffect(() => {
		if (!currentUser) {
			setIsDropdownOpen(false);
			setViewModel(undefined);
		}
		else if (ActionStatusHelper.isSucceeded(response.status)) {
			const isDisplaying = currentUser.id === Number(queryId);
			setViewModel(new UserViewModel({
				user: currentUser,
				viewEnums: UserEnums,
				accessRight,
				isDisplaying
			}));
		}
	}, [
		response.status,
		currentUser
	]);

	return {
		// Form
		viewModel,
		response,
		toggleDropdown: () => setIsDropdownOpen(prev => !prev),
		openDropdown: () => setIsDropdownOpen(true),
		closeDropdown: () => setIsDropdownOpen(false),
		isDropdownOpen,
	}
}